import { GOOGLE_DRIVE_FILE_SCOPE } from '../config/googleDriveUpload';

const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';
const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const GOOGLE_IDENTITY_LOAD_TIMEOUT_MS = 15_000;
export const GOOGLE_OAUTH_INTERACTION_TIMEOUT_MS = 2 * 60 * 1000;

interface GoogleTokenResponse {
  access_token?: string;
  expires_in?: number | string;
  scope?: string;
  error?: string;
}

interface GoogleTokenClient {
  requestAccessToken: (override?: { prompt?: '' | 'none' | 'consent' | 'select_account' }) => void;
}

interface GoogleOAuth2Api {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    include_granted_scopes: boolean;
    callback: (response: GoogleTokenResponse) => void;
    error_callback: () => void;
  }) => GoogleTokenClient;
}

interface GoogleIdentityWindow extends Window {
  google?: {
    accounts?: {
      oauth2?: GoogleOAuth2Api;
    };
  };
}

export interface GoogleDriveAccessGrant {
  accessToken: string;
  expiresAtMs: number;
}

let loadPromise: Promise<void> | null = null;

const getOAuth2Api = (): GoogleOAuth2Api | null => (
  (window as GoogleIdentityWindow).google?.accounts?.oauth2 || null
);

const isGoogleIdentityServicesReady = (): boolean => !!getOAuth2Api();

export const loadGoogleIdentityServices = (): Promise<void> => {
  if (isGoogleIdentityServicesReady()) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing || document.createElement('script');
    let settled = false;

    const cleanupListeners = () => {
      window.clearTimeout(timeoutId);
      script.removeEventListener('load', finish);
      script.removeEventListener('error', fail);
    };

    const finish = () => {
      if (settled) return;
      settled = true;
      cleanupListeners();
      if (isGoogleIdentityServicesReady()) {
        resolve();
        return;
      }
      script.remove();
      loadPromise = null;
      reject(new Error('Google authentication did not initialize'));
    };
    const fail = () => {
      if (settled) return;
      settled = true;
      cleanupListeners();
      script.remove();
      loadPromise = null;
      reject(new Error('Google authentication could not be loaded'));
    };

    const timeoutId = window.setTimeout(fail, GOOGLE_IDENTITY_LOAD_TIMEOUT_MS);
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', fail, { once: true });
    if (!existing) {
      script.id = GOOGLE_IDENTITY_SCRIPT_ID;
      script.src = GOOGLE_IDENTITY_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
};

export const requestGoogleDriveAccessToken = (
  clientId: string,
  prompt: '' | 'select_account',
): Promise<GoogleDriveAccessGrant> => {
  const oauth2 = getOAuth2Api();
  if (!oauth2) {
    return Promise.reject(new Error('Google authentication is not ready'));
  }

  return new Promise<GoogleDriveAccessGrant>((resolve, reject) => {
    let settled = false;
    const rejectOnce = (message: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(interactionTimeoutId);
      reject(new Error(message));
    };
    const interactionTimeoutId = window.setTimeout(
      () => rejectOnce('Google Drive authorization timed out'),
      GOOGLE_OAUTH_INTERACTION_TIMEOUT_MS,
    );

    let client: GoogleTokenClient;
    try {
      client = oauth2.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_DRIVE_FILE_SCOPE,
        include_granted_scopes: false,
        callback: (response) => {
          if (settled) return;
          if (response.error) {
            rejectOnce('Google Drive authorization was not completed');
            return;
          }
          const accessToken = String(response.access_token || '').trim();
          const scopes = String(response.scope || '').trim().split(/\s+/).filter(Boolean);
          const expiresInSeconds = Number(response.expires_in);
          if (
            !accessToken
            || scopes.length !== 1
            || scopes[0] !== GOOGLE_DRIVE_FILE_SCOPE
            || !Number.isFinite(expiresInSeconds)
            || expiresInSeconds <= 0
          ) {
            rejectOnce('Google Drive returned an invalid authorization response');
            return;
          }
          settled = true;
          window.clearTimeout(interactionTimeoutId);
          resolve({
            accessToken,
            expiresAtMs: Date.now() + (expiresInSeconds * 1000),
          });
        },
        error_callback: () => rejectOnce('Google Drive authorization was closed or blocked'),
      });
      client.requestAccessToken({ prompt });
    } catch (_error) {
      rejectOnce('Google Drive authorization could not be started');
    }
  });
};
